import { useLazyQuery } from "@apollo/client";
import { saveAs } from "file-saver";
import { gql } from "generated-graphql";
import { Document } from "generated-graphql/graphql";
import { useCallback, useState } from "react";

import { useAlerts } from "../components/Alerts/AlertProvider";

const GET_DOCUMENT_WITH_DOWNLOAD_LINK = gql(`
  query GetDocument($id: ID!) {
    document(id: $id) {
      id
      title
      fileExtension
      storageLink
      downloadLink
    }
  }
`);

const downloadFile = async (url: string, filename: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download document: ${response.statusText}`);
  }
  const download = await response.blob();

  saveAs(download, filename);
};

const getExtensionFromString = (candidate: string): string | null => {
  const parts = candidate.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : null;
};

const getFileNameForDownload = ({
  title,
  fileExtension,
  storageLink,
}: Pick<Document, "title" | "fileExtension" | "storageLink">) => {
  const extensionFromLink = getExtensionFromString(storageLink);
  const extensionFromTitle = getExtensionFromString(title);

  if (extensionFromTitle) {
    return title;
  } else if (fileExtension) {
    return `${title}.${fileExtension}`;
  } else if (extensionFromLink) {
    return `${title}.${extensionFromLink}`;
  } else {
    return title;
  }
};

export const useDocumentDownload = () => {
  const [downloadLink, setDownloadLink] = useState<string | undefined>();
  const [getDocument] = useLazyQuery(GET_DOCUMENT_WITH_DOWNLOAD_LINK, {
    fetchPolicy: "no-cache", // This ensures the download link is always fetched
  });
  const alerts = useAlerts();

  const fetchAndHandleDownload = useCallback(
    async ({ documentId }: { documentId: string }) => {
      const { data, error } = await getDocument({
        variables: { id: documentId },
      });

      if (!data?.document || !data.document.downloadLink || error) {
        alerts.error("An error occurred while viewing the record");
        return;
      }
      setDownloadLink(data.document.downloadLink);
      const filename = getFileNameForDownload(data.document);

      // Await the download so callers can track when the file actually
      // finishes saving (not just when the metadata query resolves), and so a
      // failed fetch surfaces an alert instead of being silently swallowed.
      try {
        await downloadFile(data.document.downloadLink, filename);
      } catch (e) {
        alerts.error(
          "An error occurred while downloading the document",
          e instanceof Error ? e : undefined
        );
      }
    },
    [getDocument, alerts]
  );

  const clearDownloadLink = () => {
    setDownloadLink(undefined);
  };

  return {
    downloadLink,
    fetchAndHandleDownload,
    clearDownloadLink,
  };
};
